// src/layouts/LeftDrawer/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useState } from 'react';
import { Drawer, Grid, Spin } from 'antd';
import { WhatsAppOutlined } from '@ant-design/icons';
import { MdOutlineWhatsapp } from 'react-icons/md';
import Conversation from 'modules/app-views/omnichannel/pages/Init/components/Conversation';
import omnichannelService from 'modules/app-views/omnichannel/service';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Title rendered in the AntD Drawer header (icon left, label center-left, X auto-right)
const DRAWER_TITLE = (
  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <WhatsAppOutlined style={{ color: '#25D366', fontSize: 18 }} />
    <span>WhatsApp</span>
  </span>
);

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const LeftDrawer = ({ open, onClose, conversationId: conversationIdProp, phone, countryCode, entityId, noPhone, crmId, entityName }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !screens.lg;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Own conversationId state: starts from prop (pre-loaded by CrmDocument) but can
  // change when the user switches source inside the embedded Conversation component.
  const [conversationId, setConversationId] = useState(conversationIdProp);
  // Incremented every time the user explicitly switches source. Used as part of
  // Conversation's key so it remounts with fresh data even when conversationId
  // doesn't change (backend may return the same id for different sources).
  const [sourceKey, setSourceKey] = useState(0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync when parent resolves the pre-loaded conversationId (fire-and-forget async)
  useEffect(() => {
    if (conversationIdProp) setConversationId(conversationIdProp);
  }, [conversationIdProp]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  const handleNavigateAway = useCallback(() => {
    // When Conversation tries to navigate away (transfer/close/archive),
    // close the drawer instead of performing a route change.
    if (onClose) onClose();
  }, [onClose]);

  // Called by Conversation when the user picks a different source in embedded mode.
  // Re-runs getOrCreateByPhone scoped to that sourceId so body/footer reflect the
  // correct conversation (different message history, window state, etc.).
  // Also persists the chosen source so the next open restores it automatically.
  // NOTE: intentionally omits linkedEntityId here — source switching must resolve
  // conversations by phone + source, not by CRM context. Passing linkedEntityId
  // causes the backend to return the same conversation regardless of source.
  const handleEmbeddedSourceChange = useCallback(async sourceId => {
    if (!phone) return;
    // Persist as preferred source for future opens (restored in CrmDocument pre-load)
    localStorage.setItem('omnichannel_preferred_source', sourceId);
    try {
      const result = await omnichannelService.getOrCreateByPhone({
        phone,
        countryCode: countryCode || 'BR',
        sourceId,
      });
      const newId = result?._id || result?.[0]?._id;
      if (newId) {
        setConversationId(newId);
        setSourceKey(prev => prev + 1);
      }
    } catch {
      // Silent fail — keep current conversation visible
    }
  }, [phone, countryCode, entityId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      closable
      title={DRAWER_TITLE}
      destroyOnHidden={false}
      styles={{
        header: { padding: '12px 16px' },
        body: { padding: 0, overflow: 'hidden', height: '100%' },
        wrapper: { width: isMobile ? '100%' : 'min(50vw, 520px)' },
      }}
      style={{ zIndex: 1001 }}
    >
      {/* .chat wrapper is required so _chat.less selectors (.chat .chat-content, etc.) apply */}
      <div className="chat" style={{ height: '100%' }}>
        {noPhone ? (
          // Contact has no phone capable of receiving WhatsApp — show informative overlay
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 12,
              opacity: 0.5,
            }}
          >
            <MdOutlineWhatsapp style={{ fontSize: 48, color: '#25D366' }} />
            <span style={{ fontSize: 15 }}>{translateX('contact_has_no_whatsapp')}</span>
          </div>
        ) : !conversationId ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Spin />
          </div>
        ) : (
          <Conversation
            key={`${conversationId || ''}-${sourceKey}`}
            conversationId={conversationId}
            onNavigateAway={handleNavigateAway}
            onEmbeddedSourceChange={handleEmbeddedSourceChange}
            embeddedDisplayName={entityName || undefined}
            embeddedCrmId={crmId}
            embedded
          />
        )}
      </div>
    </Drawer>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default LeftDrawer;
